import { useRef, useLayoutEffect, useState, useEffect, Suspense } from "react";
import { useAppStore } from "@/stores/appStore";
import * as THREE from "three";
import { getPlankAmount } from "@/services/helpers/calculatePlanks";
import Plank from "./Plank";
import useMaterial from "@/hooks/useMaterial";
import DoorPlank from "./DoorPlank";
import { config_base } from "@/data/config_base";
import useCopyTexture from "@/hooks/useCopyTexture";

//interfaces
import { DoorFrameProps } from "@/services/interfaces";

//Beams
import { BeamBottom, BeamTop, BeamVertical } from "./Beams/Beams";

interface PlanksState {
	plankWidth: number;
	planks: Array<{
		x: number;
		y: number;
		z: number;
		height: number;
		distributedHeight: THREE.Vector2;
	}>;
}

export default function Door({
	minGap,
	plankWidth,
	doorWidth,
	centerHeight,
	outerHeight,
	frameWidth,
	doorFrameThickness = 0.028,
	pillarPosition,
}: DoorFrameProps) {
	const [planks, setPlanks] = useState<PlanksState | null>(null);
	const arcRef = useRef<THREE.Mesh | null>(null);
	const beamVertical = useRef<THREE.Mesh | null>(null);
	const { gate } = useAppStore((state) => state.configurator);

	const extrudeSettings = {
		depth: doorFrameThickness,
		bevelEnabled: true,
		bevelSegments: 5,
		steps: 1,
		bevelSize: 0.0015,
		bevelThickness: 0.003,
		curveSegments: 100,
	};

	const textures = useCopyTexture(gate.selectedMaterials[gate.id]?.textures, {
		repeat: gate.selectedMaterials[gate.id]?.settings.repeat,
	});

	useLayoutEffect(() => {
		if (!arcRef) return;

		const planks = getPlankAmount(
			arcRef,
			doorWidth,
			frameWidth,
			plankWidth,
			minGap,
			doorFrameThickness,
			centerHeight,
			outerHeight
		);
		setPlanks(planks);
	}, [doorWidth, frameWidth, centerHeight, outerHeight, gate, plankWidth, arcRef]);

	return (
		<group position={[0.005, 0, -0.015]}>
			<BeamTop
				ref={arcRef}
				doorWidth={doorWidth}
				frameWidth={frameWidth}
				centerHeight={centerHeight}
				outerHeight={outerHeight}
				depth={doorFrameThickness}
				extrudeSettings={extrudeSettings}
				pillarPosition={pillarPosition}
				textures={textures}
			/>
			<BeamBottom
				doorWidth={doorWidth}
				frameWidth={frameWidth}
				centerHeight={centerHeight}
				outerHeight={outerHeight}
				depth={doorFrameThickness}
				extrudeSettings={extrudeSettings}
				pillarPosition={pillarPosition}
				textures={textures}
			/>

			<BeamVertical
				doorWidth={doorWidth}
				frameWidth={frameWidth}
				height={centerHeight}
				centerHeight={centerHeight}
				outerHeight={outerHeight}
				depth={doorFrameThickness * 1.1}
				extrudeSettings={extrudeSettings}
				position={[0, 0, -(doorFrameThickness / 2)]}
				crownPos="left"
				pillarPosition={pillarPosition}
				textures={textures}
			/>

			<BeamVertical
				ref={beamVertical}
				doorWidth={doorWidth}
				frameWidth={frameWidth}
				height={outerHeight}
				centerHeight={centerHeight}
				outerHeight={outerHeight}
				depth={doorFrameThickness * 1.1}
				extrudeSettings={extrudeSettings}
				crownPos="right"
				position={[doorWidth - frameWidth, 0, -(doorFrameThickness / 2)]}
				pillarPosition={pillarPosition}
				textures={textures}
			/>

			{/* centerdoor */}
			<Plank
				position={{ x: -0.0195, y: centerHeight / 2, z: doorFrameThickness - doorFrameThickness / 4 }}
				width={frameWidth / 2}
				height={centerHeight}
				length={0.015}
				radius={0.0008}
				bevelSegments={2}
				smoothness={0.0008}
				textures={textures}
				pillarPosition={pillarPosition}
				rotation={[0, Math.PI / 2, 0]}
			/>
			{/*  */}
			{planks &&
				planks.planks.map((plank, index) => (
					<DoorPlank
						planks={planks.planks}
						key={index}
						position={{ ...plank }}
						width={plankWidth}
						height={plank.height}
						distributedHeight={plank.distributedHeight}
						length={doorFrameThickness - 0.002}
						index={index}
						radius={0.0008}
						bevelSegments={2}
						smoothness={0.0008}
						pillarPosition={pillarPosition}
						textures={textures}
					/>
				))}
		</group>
	);
}
